/**
 * McaRefreshModal — compare a stored company against the MCA master dataset
 * and apply only the changes the user accepts.
 *
 * DELIBERATELY NOT A ONE-CLICK "SYNC". The two sources genuinely disagree in
 * the field (MCA lists GREENLAND's ROC as "ROC Mumbai"; the stored record says
 * "ROC Nagpur"), and a company record accumulates hand corrections the registry
 * doesn't carry. Silently overwriting those would destroy work with no trace,
 * so every field is opt-in and the user sees both values before choosing.
 *
 * Fields MCA does not publish — directors, auditor, charges, email, last AGM —
 * are never compared. An absent upstream value must never read as "MCA says you
 * have none of these".
 */
import { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiCheckCircle, FiAlertCircle, FiArrowRight } from 'react-icons/fi';
import Modal from './Modal';
import Button from './Button';
import { companiesAPI } from '../../services/api';
import { useUI } from '../../context/UIContext';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };

export default function McaRefreshModal({ open, company, onClose, onApplied }) {
  const { showToast } = useUI();
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [chosen, setChosen] = useState({});

  const companyId = company?._id || company?.id;

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await companiesAPI.mcaDiff(companyId);
      const data = res?.data || res;
      setResult(data);
      // Everything pre-checked: the common case is "the registry is right".
      // The user unticks what they've deliberately corrected locally.
      setChosen(Object.fromEntries((data?.differences || []).map((d) => [d.field, true])));
    } catch (e) {
      setError({ code: e?.code, message: e?.message || 'Could not compare with MCA.' });
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const differences = result?.differences || [];
  const selectedCount = differences.filter((d) => chosen[d.field]).length;

  const apply = async () => {
    const patch = {};
    for (const d of differences) if (chosen[d.field]) patch[d.field] = d.incoming;
    if (!Object.keys(patch).length) return;
    setApplying(true);
    try {
      // Routed through the normal update endpoint — validated, audit-logged,
      // and access-checked exactly like a manual edit.
      await companiesAPI.update(companyId, patch);
      showToast(`Updated ${selectedCount} field${selectedCount === 1 ? '' : 's'} from MCA`, 'success');
      onApplied?.();
      onClose?.();
    } catch (e) {
      showToast(e?.message || 'Could not apply the MCA values', 'error');
    } finally {
      setApplying(false);
    }
  };

  const Body = () => {
    if (loading) {
      return (
        // The body now carries its own py-5, so these states pad to the same
        // optical height with less of their own.
        <div className="py-6 text-center text-[13px] text-[#7a7060]">
          <FiRefreshCw size={20} className="mx-auto mb-3 animate-spin" style={{ color: '#145f5f' }} />
          Comparing with the MCA master dataset…
        </div>
      );
    }
    if (error) {
      const friendly = error.code === 'MCA_COMPANY_NOT_FOUND'
        ? 'This company isn\'t published in the MCA master dataset. Recently incorporated entities often aren\'t listed yet.'
        : error.code === 'INSUFFICIENT_COMPANY_ACCESS'
          ? error.message
          : error.message;
      return (
        <div className="py-5 text-center">
          <FiAlertCircle size={22} className="mx-auto mb-3" style={{ color: '#9a564a' }} />
          <div className="text-[13px] text-[#5a5145] max-w-[420px] mx-auto leading-relaxed">{friendly}</div>
        </div>
      );
    }
    if (result?.inSync) {
      return (
        <div className="py-6 text-center">
          <FiCheckCircle size={22} className="mx-auto mb-3" style={{ color: '#4a6b3f' }} />
          <div className="text-[13px] font-semibold text-[#08111f]">Already matches MCA</div>
          <div className="text-[12px] text-[#7a7060] mt-1.5">Every field MCA publishes is identical to your record.</div>
        </div>
      );
    }
    if (!differences.length) return null;

    return (
      <div>
        {/* Capped measure: the explanation is three sentences of real
            instruction, and full-width 12px lines are hard to come back to
            after looking away at the diff. */}
        <div className="text-[12px] text-[#7a7060] mb-5 leading-[1.65] max-w-[68ch]">
          We re-fetched this company from the MCA registry.{' '}
          <span className="font-semibold text-[#08111f]">
            {differences.length} field{differences.length === 1 ? '' : 's'} differ from your saved record.
          </span>{' '}
          Ticked fields will be updated to the MCA value; untick anything you&apos;ve corrected deliberately
          and it keeps what you have. Nothing is saved until you press Apply.
        </div>
        <div className="rounded-[12px] border overflow-hidden" style={{ borderColor: 'rgba(226,217,200,0.8)' }}>
          {differences.map((d, i) => {
            // A wrapping value (an address) pushed the arrow onto a line of its
            // own, where it read as a stray glyph between two blocks. Long
            // diffs stack instead, with the arrow leading the incoming value.
            const stacked = `${d.current || ''}${d.incoming || ''}`.length > 56;
            return (
              <label
                key={d.field}
                className="flex items-start gap-3.5 px-4 py-3.5 cursor-pointer transition-colors hover:bg-[#faf7f2]"
                style={{ borderTop: i ? '1px solid rgba(226,217,200,0.7)' : 'none' }}
              >
                <input
                  type="checkbox"
                  className="mt-[3px] shrink-0 w-[15px] h-[15px] accent-[#145f5f] cursor-pointer"
                  checked={Boolean(chosen[d.field])}
                  onChange={(e) => setChosen((p) => ({ ...p, [d.field]: e.target.checked }))}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#9a9080] mb-1.5">{d.label}</div>
                  {stacked ? (
                    <div className="text-[12.5px] leading-[1.6] space-y-1.5">
                      <div className="line-through text-[#9a564a] break-words" style={MONO}>{d.current || '(empty)'}</div>
                      <div className="flex items-start gap-2">
                        <FiArrowRight size={13} className="mt-[4px] shrink-0 text-[#7a7060]" />
                        <div className="font-semibold text-[#4a6b3f] break-words" style={MONO}>{d.incoming}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 flex-wrap text-[12.5px] leading-[1.6]">
                      <span className="line-through text-[#9a564a] break-words" style={MONO}>{d.current || '(empty)'}</span>
                      <FiArrowRight size={13} className="mt-[4px] shrink-0 text-[#7a7060]" />
                      <span className="font-semibold text-[#4a6b3f] break-words" style={MONO}>{d.incoming}</span>
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
        {result?.notPublishedByMca?.length > 0 && (
          // Ruled off as a footnote — without the separator it read as a fourth
          // row of the diff list rather than an aside about it.
          <div
            className="mt-5 pt-4 text-[11.5px] text-[#7a7060] leading-relaxed max-w-[68ch]"
            style={{ borderTop: '1px solid rgba(226,217,200,0.7)' }}
          >
            Not compared — MCA doesn&apos;t publish these:{' '}
            <span className="font-semibold">{result.notPublishedByMca.join(', ')}</span>. Keep them up to date by hand.
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Refresh company details from MCA"
      size="lg"
      // Modal's body has no padding of its own, so this content was sitting
      // flush against the dialog edge — left of its own title, which uses the
      // header's px-6. Match the header and footer gutter.
      bodyClassName="px-6 py-5"
      footer={(
        <>
          <span className="text-[11.5px] text-[#7a7060] truncate">
            {company?.name} · <span style={MONO}>{company?.cin}</span>
          </span>
          <span className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
            {differences.length > 0 && (
              <Button size="sm" onClick={apply} loading={applying} disabled={applying || selectedCount === 0}>
                Apply {selectedCount || ''} change{selectedCount === 1 ? '' : 's'}
              </Button>
            )}
          </span>
        </>
      )}
      footerClassName="justify-between"
    >
      <Body />
    </Modal>
  );
}
